import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Pencil, X, Check, Loader2, Eye, EyeOff, Palette, LayoutDashboard } from "lucide-react";
import { useEditMode } from "../EditModeContext";
import { useAuth, authHeaders } from "../AuthContext";
import { useSettings } from "../SettingsContext";
import ImagePicker from "./ImagePicker";
import FontPicker from "./FontPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRESETS = [
  { name: "Queensway (default)", primary: "", accent: "", background: "" },
  { name: "Rose gold", primary: "24 62% 55%", accent: "45 32% 55%", background: "0 0% 4%" },
  { name: "Emerald", primary: "160 45% 42%", accent: "170 25% 55%", background: "0 0% 5%" },
  { name: "Sapphire", primary: "215 60% 55%", accent: "200 30% 58%", background: "0 0% 4%" },
  { name: "Champagne on ivory", primary: "36 45% 45%", accent: "30 20% 60%", background: "36 30% 96%" },
  { name: "Deep aubergine", primary: "300 30% 55%", accent: "320 25% 65%", background: "290 15% 6%" },
];

/**
 * Floating admin toolbar (bottom-left) + shared modals:
 *  - Text editor (opened by EditableText)
 *  - Image editor (opened by EditableImage)
 *  - Design panel (colours / background) opened from the toolbar
 */
export default function InlineEditor() {
  const { editMode, toggle, target, setTarget, isAdmin } = useEditMode();
  const { token } = useAuth();
  const { settings, refresh } = useSettings();
  const [showDesign, setShowDesign] = useState(false);

  if (!isAdmin) return null;

  const put = async (payload) => {
    await axios.put(`${API}/admin/settings`, payload, { headers: authHeaders(token) });
    await refresh();
  };

  return (
    <>
      <div
        data-testid="edit-mode-toolbar"
        className="fixed bottom-6 left-6 z-[95] flex items-center gap-2 pl-4 pr-2 py-2 rounded-full bg-foreground text-background shadow-xl border border-foreground/20"
      >
        <Pencil size={13} strokeWidth={1.4} />
        <span className="text-[0.65rem] tracking-[0.28em] uppercase hidden sm:inline">Edit mode</span>
        <button
          type="button"
          data-testid="edit-mode-toggle"
          onClick={toggle}
          aria-pressed={editMode}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.6rem] tracking-widest uppercase transition-colors ${
            editMode ? "bg-primary text-primary-foreground" : "bg-background/10 hover:bg-background/20"
          }`}
        >
          {editMode ? <Eye size={11} strokeWidth={1.4} /> : <EyeOff size={11} strokeWidth={1.4} />}
          {editMode ? "On" : "Off"}
        </button>
        <button
          type="button"
          data-testid="edit-mode-design-btn"
          onClick={() => setShowDesign(true)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.6rem] tracking-widest uppercase bg-background/10 hover:bg-background/20 transition-colors"
        >
          <Palette size={11} strokeWidth={1.4} /> Design
        </button>
        <Link
          to="/admin"
          data-testid="edit-mode-admin-link"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.6rem] tracking-widest uppercase bg-background/10 hover:bg-background/20 transition-colors"
        >
          <LayoutDashboard size={11} strokeWidth={1.4} /> Admin
        </Link>
      </div>

      {target && target.type === "image" && (
        <ImageModal target={target} close={() => setTarget(null)} settings={settings} put={put} />
      )}
      {target && target.type !== "image" && (
        <TextModal target={target} close={() => setTarget(null)} put={put} />
      )}
      {showDesign && (
        <DesignModal close={() => setShowDesign(false)} settings={settings} put={put} />
      )}
    </>
  );
}

function ModalShell({ overline, title, close, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 bg-background/90 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div className="w-full max-w-3xl bg-background border border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
          <div>
            <p className="qw-overline">{overline}</p>
            <h3 className="font-serif text-2xl text-foreground">{title}</h3>
          </div>
          <button onClick={close} data-testid="inline-editor-close" className="text-foreground/60 hover:text-primary" aria-label="Close">
            <X size={20} strokeWidth={1.2} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ close, save, saving, error }) {
  return (
    <>
      {error && <p className="px-6 pb-2 text-sm text-destructive">{error}</p>}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
        <button type="button" onClick={close} data-testid="inline-editor-cancel"
          className="text-[0.65rem] tracking-wider uppercase px-4 py-2 text-foreground/70 hover:text-primary">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid="inline-editor-save"
          className="qw-btn qw-btn-primary !py-2.5 !px-5 !text-[0.7rem] disabled:opacity-60">
          {saving ? (<><Loader2 size={12} strokeWidth={1.3} className="animate-spin" /> Saving…</>) : (<><Check size={12} strokeWidth={1.3} /> Save</>)}
        </button>
      </div>
    </>
  );
}

function TextModal({ target, close, put }) {
  const [enDraft, setEnDraft] = useState(target.currentEn ?? "");
  const [frDraft, setFrDraft] = useState(target.currentFr ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    // Only persist the language fields the admin actually changed,
    // so an EN-only edit doesn't freeze the FR i18n fallback.
    const payload = {};
    if (enDraft !== (target.currentEn ?? "")) payload[target.kEn] = enDraft;
    if (frDraft !== (target.currentFr ?? "")) payload[target.kFr] = frDraft;
    if (Object.keys(payload).length === 0) { close(); return; }
    setSaving(true); setError("");
    try {
      await put(payload);
      close();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell overline="Editing" title={target.label} close={close} testid="inline-editor-modal">
      <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="qw-overline block mb-2">English</label>
          <textarea data-testid="inline-editor-en" rows="5"
            className="qw-input resize-y font-serif text-base leading-relaxed"
            value={enDraft} onChange={(e) => setEnDraft(e.target.value)} autoFocus />
        </div>
        <div>
          <label className="qw-overline block mb-2">Français</label>
          <textarea data-testid="inline-editor-fr" rows="5"
            className="qw-input resize-y font-serif text-base leading-relaxed"
            value={frDraft} onChange={(e) => setFrDraft(e.target.value)} />
        </div>
      </div>
      <ModalFooter close={close} save={save} saving={saving} error={error} />
    </ModalShell>
  );
}

function ImageModal({ target, close, settings, put }) {
  const key = `img_${target.imgKey}`;
  const [draft, setDraft] = useState(settings[key] || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true); setError("");
    try {
      await put({ [key]: draft });
      close();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell overline="Editing photo" title={target.label || "Photo"} close={close} testid="inline-image-modal">
      <div className="px-6 py-6">
        <ImagePicker value={draft} onChange={setDraft} label="" testid="inline-image-picker" />
        <p className="text-xs text-muted-foreground mt-4">Upload a new photo or pick one from the media library, then press Save. Remove restores the default photo.</p>
      </div>
      <ModalFooter close={close} save={save} saving={saving} error={error} />
    </ModalShell>
  );
}

function DesignModal({ close, settings, put }) {
  const [d, setD] = useState({
    color_primary_hsl: settings.color_primary_hsl || "",
    color_accent_hsl: settings.color_accent_hsl || "",
    color_background_hsl: settings.color_background_hsl || "",
    font_heading: settings.font_heading || "",
    font_body: settings.font_body || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const preview = (hsl, fb) => (hsl && hsl.trim() ? `hsl(${hsl})` : `hsl(var(${fb}))`);

  const save = async () => {
    setSaving(true); setError("");
    try {
      await put(d);
      close();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <ModalShell overline="Design" title="Colours, background & fonts" close={close} testid="inline-design-modal">
      <div className="px-6 py-6">
        <h4 className="qw-overline mb-3">Presets</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-testid="inline-design-presets">
          {PRESETS.map((p) => (
            <button key={p.name} type="button"
              data-testid={`inline-preset-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setD({ color_primary_hsl: p.primary, color_accent_hsl: p.accent, color_background_hsl: p.background })}
              className="border border-border p-3 text-left hover:border-primary transition-colors">
              <div className="flex gap-2 mb-2">
                <span className="w-5 h-5 rounded-full border border-border" style={{ background: preview(p.primary, "--primary") }} />
                <span className="w-5 h-5 rounded-full border border-border" style={{ background: preview(p.accent, "--accent") }} />
                <span className="w-5 h-5 rounded-full border border-border" style={{ background: preview(p.background, "--background") }} />
              </div>
              <p className="text-xs text-foreground/85">{p.name}</p>
            </button>
          ))}
        </div>

        <h4 className="qw-overline mb-3">Custom (HSL — e.g. 24 62% 55%)</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            ["color_primary_hsl", "Primary"],
            ["color_accent_hsl", "Accent"],
            ["color_background_hsl", "Background"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-[0.6rem] tracking-widest uppercase text-muted-foreground block mb-2">{label}</label>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full border border-border shrink-0"
                  style={{ background: d[k] && d[k].trim() ? `hsl(${d[k]})` : `hsl(var(--${label.toLowerCase()}))` }} />
                <input data-testid={`inline-design-${k}`} value={d[k]}
                  onChange={(e) => setD((prev) => ({ ...prev, [k]: e.target.value }))}
                  placeholder="default" className="qw-input !py-2 text-sm" />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-4 mb-8">Leave a field empty to restore the site default. Changes apply everywhere immediately after saving.</p>

        <h4 className="qw-overline mb-3">Headline font</h4>
        <div className="mb-8">
          <FontPicker kind="heading" value={d.font_heading} onChange={(v) => setD((prev) => ({ ...prev, font_heading: v }))} />
        </div>
        <h4 className="qw-overline mb-3">Body font</h4>
        <FontPicker kind="body" value={d.font_body} onChange={(v) => setD((prev) => ({ ...prev, font_body: v }))} />
      </div>
      <ModalFooter close={close} save={save} saving={saving} error={error} />
    </ModalShell>
  );
}
